import React from 'react';
import { Link, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import usePermissions from '../../hooks/usePermissions';
import {
    Box, Paper, Stack, Tabs, Tab, TextField, InputAdornment, IconButton,
    Typography, Divider, Button, ButtonBase,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import AnalyticsIcon from '@mui/icons-material/QueryStats';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BadgeIcon from '@mui/icons-material/Badge';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import EuroIcon from '@mui/icons-material/Euro';
import TableChartIcon from '@mui/icons-material/TableChart';
import GroupsIcon from '@mui/icons-material/Groups';
import TimerIcon from '@mui/icons-material/Timer';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import StarIcon from '@mui/icons-material/Star';
import SavingsIcon from '@mui/icons-material/Savings';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import PolicyIcon from '@mui/icons-material/Policy';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TuneIcon from '@mui/icons-material/Tune';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import PaymentsIcon from '@mui/icons-material/Payments';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

// ─────────────────────────────────────────────────────────────────────────────
// Report registry
//
// One declarative list drives the tabs, the section headers, the counters and
// the search. Adding a report means adding a row here — nothing else. Each item
// carries only its translation stem: the label is `reports.<key>` and the
// description `reports.<key>Desc`, so the JSX never repeats the i18n plumbing.
//
// Colour is a property of the GROUP, not of the individual report. The theme's
// rule is one brand accent + neutrals, with semantic colours reserved for
// status; a per-card rainbow made the hub read as decoration. A single accent
// per group turns colour back into information — it tells you which family a
// report belongs to.
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
    { id: 'management',  labelKey: 'reports.tabManagement'  },
    { id: 'atk',         labelKey: 'reports.tabATK'         },
    { id: 'inspectorat', labelKey: 'reports.tabInspectorat' },
    { id: 'compliance',  labelKey: 'reports.tabCompliance'  },
];

const GROUPS = [
    {
        id: 'analytics', tab: 'management', color: '#2563EB',
        labelKey: 'reports.sectionAnalytics',
        items: [
            { key: 'analytics',        to: '/home/reports/analytics',        icon: AnalyticsIcon },
            { key: 'workerEvaluation', to: '/home/reports/worker-evaluation', icon: StarIcon },
            // "Raporti i detyrave" — hequr përkohësisht bashkë me modulin e
            // detyrave (shih sidebarConfig.js); rruga mbetet e gjallë.
        ],
    },
    {
        id: 'time', tab: 'management', color: '#0891B2',
        labelKey: 'reports.sectionTimeTracking',
        items: [
            { key: 'workHours',    to: '/home/reports/work-hours',    icon: AccessTimeIcon },
            { key: 'attendance',   to: '/home/reports/attendance',    icon: EventAvailableIcon },
            { key: 'overtime',     to: '/home/reports/overtime',      icon: MoreTimeIcon },
            { key: 'projectHours', to: '/home/reports/project-hours', icon: FolderOpenIcon },
        ],
    },
    {
        id: 'payroll', tab: 'management', color: '#0F766E',
        labelKey: 'reports.sectionPayroll',
        items: [
            { key: 'laborCost',          to: '/home/reports/hr-spend',          icon: AccountBalanceWalletIcon },
            { key: 'payroll',            to: '/home/reports/payroll',           icon: PaymentsIcon },
            { key: 'payrollAdjustments', to: '/payroll-adjustments',            icon: TuneIcon },
            { key: 'monthlyCost',        to: '/home/reports/monthly-costs',     icon: GroupsIcon },
            { key: 'creditorMonthly',    to: '/home/reports/creditor-monthly',  icon: ReceiptLongIcon },
        ],
    },
    {
        id: 'employees', tab: 'management', color: '#7C3AED',
        labelKey: 'reports.sectionEmployees',
        items: [
            { key: 'employeeDetail',     to: '/home/reports/employee-detail',  icon: AssignmentIndIcon },
            { key: 'employeeSummary',    to: '/home/reports/employee-summary', icon: SummarizeIcon },
            { key: 'employeeCard',       to: '/home/reports/employees-cards',  icon: BadgeIcon },
            { key: 'leaveKS',            to: '/home/reports/leave-ks',         icon: BeachAccessIcon },
            { key: 'leaveBalance',       to: '/home/reports/leave-balance',    icon: BadgeIcon },
            { key: 'warnings',           to: '/warnings',                      icon: WarningAmberIcon },
            { key: 'applicationsReport', to: '/applications/reports',          icon: WorkOutlineIcon },
        ],
    },
    {
        id: 'atk-payroll', tab: 'atk', color: '#1D4ED8',
        labelKey: 'reports.sectionATKPayroll',
        items: [
            { key: 'atkRegistrations', to: '/home/reports/atk-registrations', icon: CheckCircleIcon },
            { key: 'payrollList',      to: '/home/reports/local-payroll-ks',  icon: EuroIcon },
            { key: 'atkTM',            to: '/home/reports/atk-tm',            icon: TableChartIcon },
        ],
    },
    {
        id: 'presence', tab: 'atk', color: '#0284C7',
        labelKey: 'reports.sectionPresence',
        items: [
            { key: 'presenceInspectorat', to: '/home/reports/presence',       icon: TimerIcon },
            { key: 'presenceLogs',        to: '/home/reports/presence-logs',  icon: HistoryIcon },
            { key: 'kbpk',                to: '/home/reports/kbpk',           icon: SavingsIcon },
        ],
    },
    {
        id: 'inspectorat', tab: 'inspectorat', color: '#B45309',
        labelKey: 'reports.sectionInspectorat',
        items: [
            { key: 'inspectoratWorkers',  to: '/home/reports/inspektorat',   icon: PeopleAltIcon },
            { key: 'inspectoratPresence', to: '/home/reports/presence',      icon: TimerIcon },
            { key: 'activeContracts',     to: '/contractlist',               icon: AssignmentIcon },
            { key: 'takenLeaves',         to: '/home/reports/leave-balance', icon: BeachAccessIcon },
        ],
    },
    {
        id: 'compliance', tab: 'compliance', color: '#15803D',
        labelKey: 'reports.sectionCompliance',
        items: [
            { key: 'complianceRisk',   to: '/compliance',                     icon: GppMaybeIcon },
            { key: 'auditLog',         to: '/audit-log',                      icon: PolicyIcon },
            { key: 'atkRegistrations', to: '/home/reports/atk-registrations', icon: CheckCircleIcon },
            { key: 'kbpkReport',       to: '/home/reports/kbpk',              icon: SavingsIcon },
        ],
    },
];

const TAB_COUNTS = TABS.reduce((acc, tb) => {
    acc[tb.id] = GROUPS.filter((g) => g.tab === tb.id).reduce((n, g) => n + g.items.length, 0);
    return acc;
}, {});

/**
 * Accent-insensitive search key. Albanian labels are full of ë/ç and nobody
 * types them into a filter box — "vleresim" must find "Vlerësimi i Punëtorit".
 */
const norm = (s) => (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

function ReportTile({ to, icon: Icon, label, description, color }) {
    return (
        <ButtonBase
            component={Link}
            to={to}
            sx={(theme) => ({
                width: '100%',
                height: '100%',
                p: 1.15,
                gap: 1.15,
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                transition: theme.motion?.hover,
                '&:hover': {
                    borderColor: alpha(color, 0.5),
                    bgcolor: alpha(color, 0.04),
                },
                ...theme.focusRing,
            })}
        >
            <Box
                sx={{
                    width: 30,
                    height: 30,
                    flexShrink: 0,
                    borderRadius: 1.5,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: alpha(color, 0.1),
                    color,
                }}
            >
                <Icon sx={{ fontSize: 16 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.775rem', lineHeight: 1.25, color: 'text.primary' }}>
                    {label}
                </Typography>
                {description && (
                    <Typography
                        noWrap
                        title={description}
                        sx={{ fontSize: '0.67rem', lineHeight: 1.35, mt: 0.15, color: 'text.secondary' }}
                    >
                        {description}
                    </Typography>
                )}
            </Box>
        </ButtonBase>
    );
}

const Reports = () => {
    const { t } = useTranslation();
    const { isWorkerOnly } = usePermissions();
    const [tab, setTab] = React.useState('management');
    const [search, setSearch] = React.useState('');

    // Resolve every label once per language change, so the filter below can
    // match against the text the user actually sees.
    const groups = React.useMemo(() => GROUPS.map((g) => ({
        ...g,
        label: t(g.labelKey),
        items: g.items.map((it) => ({
            ...it,
            id: `${g.id}-${it.key}`,
            label: t(`reports.${it.key}`),
            description: t(`reports.${it.key}Desc`),
        })),
    })), [t]);

    const query = norm(search);

    // Searching deliberately ignores the active tab — with ~30 reports spread
    // over four tabs, a search that only looked inside the current one would
    // report "nothing found" while the report sits one tab away.
    const visible = React.useMemo(() => groups
        .filter((g) => (query ? true : g.tab === tab))
        .map((g) => (query
            ? { ...g, items: g.items.filter((it) => norm(g.label).includes(query)
                || norm(it.label).includes(query)
                || norm(it.description).includes(query)) }
            : g))
        .filter((g) => g.items.length > 0), [groups, query, tab]);

    const total = visible.reduce((n, g) => n + g.items.length, 0);

    // The management Reports hub (staff turnover, labour cost, company payroll,
    // employee cards, etc.) is for managers / HR / admins only. Regular workers
    // reach their own reports through dedicated routes — "Profili im"
    // (/home/reports/employee-detail), "Orari Mujor Individual" and "Avancet e
    // mia" — which are matched before this hub in the router, so this redirect
    // never blocks an employee's own self-service reports. It is a role-based
    // safety net: even if a worker account carries a stray department-scoped
    // reports permission, it can no longer see the management hub.
    if (isWorkerOnly) {
        return <Redirect to="/" />;
    }

    return (
        <Box sx={{ minWidth: 0 }}>
            {/* Toolbar — tabs and search share one row so the hub opens with the
                report grid already on screen instead of below the fold. */}
            <Paper
                variant="outlined"
                sx={{
                    px: 1,
                    py: 0.5,
                    mb: 1.25,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                }}
            >
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        flex: '1 1 auto',
                        minHeight: 34,
                        '& .MuiTabs-indicator': { height: 2, borderRadius: 1 },
                        '& .MuiTab-root': {
                            minHeight: 34,
                            py: 0.5,
                            px: 1.25,
                            fontWeight: 600,
                            fontSize: '0.78rem',
                            textTransform: 'none',
                            color: 'text.secondary',
                        },
                        '& .Mui-selected': { color: 'primary.main' },
                    }}
                >
                    {TABS.map((tb) => (
                        <Tab
                            key={tb.id}
                            value={tb.id}
                            label={(
                                <Stack direction="row" alignItems="center" spacing={0.6}>
                                    <span>{t(tb.labelKey)}</span>
                                    <Box
                                        component="span"
                                        sx={{
                                            px: 0.55,
                                            borderRadius: 0.75,
                                            fontSize: '0.62rem',
                                            fontWeight: 700,
                                            lineHeight: 1.7,
                                            color: 'text.secondary',
                                            bgcolor: 'action.hover',
                                        }}
                                    >
                                        {TAB_COUNTS[tb.id]}
                                    </Box>
                                </Stack>
                            )}
                        />
                    ))}
                </Tabs>

                <TextField
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('reports.searchPlaceholder')}
                    inputProps={{ 'aria-label': t('reports.searchPlaceholder') }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" aria-label={t('reports.clearSearch')} onClick={() => setSearch('')}>
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{
                        width: { xs: '100%', sm: 236 },
                        '& .MuiOutlinedInput-root': { height: 32, borderRadius: 1.5, fontSize: '0.78rem' },
                    }}
                />
            </Paper>

            <Typography
                variant="caption"
                aria-live="polite"
                sx={{ display: 'block', textAlign: 'right', mb: 0.75, px: 0.25, fontSize: '0.67rem', color: 'text.disabled' }}
            >
                {t('reports.countLabel', { count: total })}
                {query ? ` · ${t('reports.searchAllHint')}` : ''}
            </Typography>

            {total === 0 ? (
                <Paper variant="outlined" sx={{ borderRadius: 2, py: 5, px: 2, textAlign: 'center' }}>
                    <SearchOffIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
                    <Typography sx={{ mt: 0.75, fontWeight: 700, fontSize: '0.85rem' }}>
                        {t('reports.noResultsTitle')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {t('reports.noResultsHint')}
                    </Typography>
                    <Button size="small" onClick={() => setSearch('')} sx={{ mt: 1.25, textTransform: 'none', fontWeight: 600 }}>
                        {t('reports.clearSearch')}
                    </Button>
                </Paper>
            ) : visible.map((g) => (
                <Box key={g.id} component="section" sx={{ mb: 1.75 }}>
                    <Stack direction="row" alignItems="center" spacing={0.85} sx={{ mb: 0.75 }}>
                        <Box sx={{ width: 3, height: 12, borderRadius: 1, bgcolor: g.color, flexShrink: 0 }} />
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}
                        >
                            {g.label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
                            {g.items.length}
                        </Typography>
                        <Divider sx={{ flex: 1, ml: 0.25 }} />
                    </Stack>

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 1,
                            gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0, 1fr))',
                                sm: 'repeat(auto-fill, minmax(224px, 1fr))',
                            },
                        }}
                    >
                        {g.items.map((it) => (
                            <ReportTile
                                key={it.id}
                                to={it.to}
                                icon={it.icon}
                                label={it.label}
                                description={it.description}
                                color={g.color}
                            />
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export default Reports;
